import React, { useEffect, useRef, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { L } from "@/components/ideaos/shared";

export const SCORE_BANDS = [["0-20", 0, 20], ["20-50", 20, 50], ["50-75", 50, 75], ["75-100", 75, 101]];
export const TYPE_FILTERS = {
  favorites: (p) => !!p.favorite,
  shared: (p) => (p.collaborators || []).length > 0,
  sold: (p) => !!p.sold,
  purchased: (p) => !!p.purchased,
  market: (p) => !!p.listing_id || p.visibility === "marketplace",
  community: (p) => ["community", "public"].includes(p.visibility),
  hidden: (p) => !p.visibility || ["private", "selected"].includes(p.visibility),
  scored: (p) => p.score?.total != null,
};
export const SORTS = {
  recent: (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
  popular: (a, b) => (b.reactions_count || 0) - (a.reactions_count || 0),
  score: (a, b) => (b.score?.total ?? -1) - (a.score?.total ?? -1),
};
export const DEFAULT_FILTERS = { band: null, types: [], sort: "recent" };

export function applyFilters(list, f) {
  const band = SCORE_BANDS.find((b) => b[0] === f.band);
  const sort = SORTS[f.sort] || SORTS.recent;
  return list
    .filter((p) => !band || (p.score?.total != null && p.score.total >= band[1] && p.score.total < band[2]))
    .filter((p) => f.types.every((k) => TYPE_FILTERS[k](p)))
    .sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0) || sort(a, b));
}

const chip = (on) => `rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#0b0f1a] bg-[#0b0f1a] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`;

/** Filters button + popover: score bands, idea types (collab / sold / purchased / market / scored) and sort (recent / popular / score). */
export function VaultFilters({ value, onChange, projects }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  const active = (value.band ? 1 : 0) + value.types.length + (value.sort !== "recent" ? 1 : 0);
  const count = (fn) => projects.filter(fn).length;
  const TYPES = [["favorites", L(lang, "Preferite", "Favorites")], ["shared", L(lang, "In collaborazione", "In collaboration")], ["sold", L(lang, "Vendute", "Sold")], ["purchased", L(lang, "Acquistate", "Purchased")], ["community", L(lang, "In community", "In community")], ["market", L(lang, "In Market", "On the market")], ["hidden", L(lang, "Nascoste", "Hidden")], ["scored", L(lang, "Con punteggio", "Scored")]];
  const SORT_LABELS = [["recent", L(lang, "Recenti", "Recent")], ["popular", L(lang, "Più popolari", "Most popular")], ["score", L(lang, "Punteggio", "Score")]];
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="vault-filters-btn" aria-label={L(lang, "Filtri", "Filters")}
        className={`inline-flex h-10 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-colors ${active ? "bg-[#0b0f1a] text-white" : "bg-slate-100 text-slate-600 hover:text-[#0b0f1a]"}`}>
        <SlidersHorizontal size={14} /> {L(lang, "Filtri", "Filters")}{active > 0 && <span className="ml-0.5 rounded-full bg-white/20 px-1.5 text-[10px]" data-testid="vault-filters-count">{active}</span>}
      </button>
      {open && (
        <div role="dialog" data-testid="vault-filters-panel" className="absolute right-0 top-12 z-40 w-[min(22rem,calc(100vw-2.5rem))] max-md:fixed max-md:left-1/2 max-md:top-1/2 max-md:-translate-x-1/2 max-md:-translate-y-1/2 rounded-3xl bg-white p-4 shadow-[0_24px_60px_-28px_rgba(11,15,26,0.45)] ring-1 ring-slate-200">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#0b0f1a]">{L(lang, "Filtra le idee", "Filter ideas")}</p>
            <div className="flex items-center gap-2">
              {active > 0 && <button type="button" onClick={() => onChange(DEFAULT_FILTERS)} className="text-[11px] font-semibold text-[#7c3aed]" data-testid="vault-filters-reset">{L(lang, "Azzera", "Reset")}</button>}
              <button type="button" onClick={() => setOpen(false)} aria-label="close" className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-[#f4f6fa]"><X size={14} /></button>
            </div>
          </div>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{L(lang, "Fascia di punteggio", "Score band")}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {SCORE_BANDS.map(([k, lo, hi]) => <button key={k} type="button" onClick={() => onChange({ ...value, band: value.band === k ? null : k })} aria-pressed={value.band === k} data-testid={`vault-band-${k}`} className={chip(value.band === k)}>{k} <span className="opacity-50">· {count((p) => p.score?.total != null && p.score.total >= lo && p.score.total < hi)}</span></button>)}
          </div>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{L(lang, "Tipo", "Type")}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {TYPES.map(([k, label]) => { const on = value.types.includes(k); return <button key={k} type="button" onClick={() => onChange({ ...value, types: on ? value.types.filter((x) => x !== k) : [...value.types, k] })} aria-pressed={on} data-testid={`vault-type-${k}`} className={chip(on)}>{label} <span className="opacity-50">· {count(TYPE_FILTERS[k])}</span></button>; })}
          </div>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{L(lang, "Ordina per", "Sort by")}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {SORT_LABELS.map(([k, label]) => <button key={k} type="button" onClick={() => onChange({ ...value, sort: k })} aria-pressed={value.sort === k} data-testid={`vault-sort-${k}`} className={chip(value.sort === k)}>{label}</button>)}
          </div>
        </div>
      )}
    </div>
  );
}
